import { AuthFlowType, StytchAPIError, StytchEvent, StytchProjectConfigurationInput } from '@stytch/core/public';
import React, { ComponentType, Dispatch, ReactNode, useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';

import { HandledTokenType } from '../../b2b/StytchB2BClient';
import { StytchB2BExtendedLoginConfig } from '../../types';
import { extractErrorType } from '../../utils/extractErrorType';
import Logo from '../components/atoms/Logo';
import { ErrorScreen } from '../components/molecules/ErrorScreen';
import { LoggingInScreen } from '../components/molecules/Loading';
import MainContainer from '../components/molecules/MainContainer';
import { usePresentation } from '../components/PresentationConfig';
import { B2BGoogleOneTap } from './components/B2BOneTap';
import { useConfig, useErrorCallback, useEventCallback, useGlobalReducer, useStytch } from './GlobalContextProvider';
import { useIsOnlyFloatingOneTap } from './hooks/useIsOnlyFloatingOneTap';
import { Action } from './reducer';
import { ConfirmationScreen } from './screens/Confirmation';
import { Discovery } from './screens/Discovery';
import { MainScreen } from './screens/Main';
import { MFAEnrollmentSelectionScreen } from './screens/MFAEnrollmentSelectionScreen';
import { RecoveryCodeEntryScreen } from './screens/RecoveryCodeEntryScreen';
import { RecoveryCodeSaveScreen } from './screens/RecoveryCodeSaveScreen';
import { SMSOTPEnrollScreen } from './screens/SMSOTPEnrollScreen';
import { SMSOTPEntryScreen } from './screens/SMSOTPEntryScreen';
import { TOTPEnrollManualScreen } from './screens/TOTPEnrollManualScreen';
import { TOTPEnrollQRScreen } from './screens/TOTPEnrollQRScreen';
import { TOTPEntryScreen } from './screens/TOTPEntryScreen';
import { AppScreens } from './types/AppScreens';
import { ErrorType } from './types/ErrorType';
import {
  getStytchEventByKey,
  onAuthenticateSuccess,
  onDiscoveryAuthenticateSuccess,
  StytchMutationKey,
  useExtractSlug,
  useProductComponents,
} from './utils';

/**
 * Separately store additional things to do when authenticateByUrl completes or errors
 */
type AuthTokenConfig = {
  mutationKey: StytchMutationKey;
  // Typing this is too hard. Instead, manually verify the typings from the client matches that expected
  // by these success functions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess: (data: any, dispatch: Dispatch<Action>, config: StytchB2BExtendedLoginConfig) => void;
  defaultError?: ErrorType;
};

const authTokenConfig: Record<HandledTokenType, AuthTokenConfig> = {
  discovery: {
    mutationKey: 'stytch.magicLinks.discovery.authenticate',
    onSuccess: onDiscoveryAuthenticateSuccess,
    defaultError: ErrorType.EmailMagicLink,
  },
  discovery_oauth: {
    mutationKey: 'stytch.oauth.discovery.authenticate',
    onSuccess: onDiscoveryAuthenticateSuccess,
  },
  oauth: {
    mutationKey: 'stytch.oauth.authenticate',
    onSuccess: onAuthenticateSuccess,
  },
  sso: {
    mutationKey: 'stytch.sso.authenticate',
    onSuccess: onAuthenticateSuccess,
  },
  multi_tenant_magic_links: {
    mutationKey: 'stytch.magicLinks.authenticate',
    onSuccess: onAuthenticateSuccess,
    defaultError: ErrorType.EmailMagicLink,
  },
  multi_tenant_impersonation: {
    mutationKey: 'stytch.impersonation.authenticate',
    onSuccess: onAuthenticateSuccess,
  },
};

const errorTypeMap: Record<string, ErrorType> = {
  ad_blocker_detected: ErrorType.AdBlockerDetected,
};

const AppContainer: () => ReactNode = () => {
  const [state, dispatch] = useGlobalReducer();
  const stytchClient = useStytch();
  const triggerError = useErrorCallback();
  const triggerEvent = useEventCallback();
  const config = useConfig();
  const { displayWatermark, theme, options } = usePresentation();
  const isOnlyFloatingOneTap = useIsOnlyFloatingOneTap();

  const currentScreen = state.screen;

  const screenComponentMap = useProductComponents(config, 'screens')!;

  const handleStytchAPIError = (error: StytchAPIError, defaultErrorType = ErrorType.Default) => {
    const error_type = extractErrorType(error);
    const errorType: ErrorType = errorTypeMap[error_type!] ?? defaultErrorType;

    dispatch({
      type: 'set_error_message_and_transition',
      errorType,
      canGoBack: false,
    });
  };

  const [isTokenAuthLoading, setIsTokenAuthLoading] = useState(false);
  const { mutate } = useSWRConfig();

  const { slug, resultPending } = useExtractSlug();

  useEffect(() => {
    // If we're in One Tap only mode, we should not run any other effects
    if (isOnlyFloatingOneTap) {
      return;
    }

    // Password reset has special handling for exchanging a magic link token
    // for a session and then using it to reset a password
    if (state.flowState.type == AuthFlowType.PasswordReset) {
      return;
    }

    const parsed = stytchClient.parseAuthenticateUrl();
    if (!parsed?.handled) {
      return;
    }

    const { mutationKey, defaultError, onSuccess } = authTokenConfig[parsed.tokenType];
    setIsTokenAuthLoading(true);
    mutate(
      mutationKey,
      stytchClient.authenticateByUrl({ session_duration_minutes: config.sessionOptions.sessionDurationMinutes }),
    )
      .then(
        (result) => {
          if (!result?.handled) return; // Should not be possible
          const { data } = result;

          const event = getStytchEventByKey(mutationKey);
          if (event) {
            triggerEvent({ type: event, data } as StytchEvent<StytchProjectConfigurationInput>);
          }

          onSuccess(data, dispatch, config);
        },
        (error) => {
          triggerError(error);
          handleStytchAPIError(error, defaultError);
        },
      )
      .finally(() => setIsTokenAuthLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps -- SDK-1354
  }, []);

  useEffect(() => {
    // If we're in One Tap only mode, we should not run any other effects
    if (isOnlyFloatingOneTap) {
      return;
    }

    // Skip further action if token discovery is in progress
    if (isTokenAuthLoading) {
      return;
    }

    const parsed = stytchClient.parseAuthenticateUrl();
    if (parsed?.handled) {
      return;
    }

    if (
      !slug &&
      !resultPending &&
      state.flowState.organization === null &&
      state.screen === AppScreens.Main &&
      state.flowState.type == AuthFlowType.Organization
    ) {
      // No slug found (or no slug pattern)
      dispatch({
        type: 'set_error_message_and_transition',
        errorType: ErrorType.Organization,
        canGoBack: false,
      });
    }
  }, [
    dispatch,
    isOnlyFloatingOneTap,
    isTokenAuthLoading,
    resultPending,
    slug,
    state.flowState.organization,
    state.flowState.type,
    state.screen,
    stytchClient,
    triggerError,
  ]);

  if (isOnlyFloatingOneTap) {
    return <B2BGoogleOneTap />;
  }

  if (isTokenAuthLoading) {
    return (
      <MainContainer theme={theme} displayWatermark={false}>
        <LoggingInScreen />
      </MainContainer>
    );
  }

  let orgLogo;
  if (state.flowState.organization?.organization_logo_url) {
    const { organization_name, organization_logo_url } = state.flowState.organization;
    orgLogo = {
      url: organization_logo_url,
      alt: organization_name ?? '',
    };
  }

  const defaultScreenComponents: Partial<Record<AppScreens, ComponentType>> = {
    // Main is omitted from StytchB2BProduct since it's used by everything
    [AppScreens.Main]: MainScreen,
    [AppScreens.LoggedIn]: ConfirmationScreen,

    // Discovery can be opted into dynamically via stytch_redirect_type ResetPassword flow
    // so it cannot be statically shaken away
    [AppScreens.Discovery]: Discovery,
    [AppScreens.Error]: ErrorScreen,

    // MFA products are dynamically configured depending on organization and user choice,
    // and opted into by default
    [AppScreens.MFAEnrollmentSelection]: MFAEnrollmentSelectionScreen,
    [AppScreens.SMSOTPEnrollment]: SMSOTPEnrollScreen,
    [AppScreens.SMSOTPEntry]: SMSOTPEntryScreen,
    [AppScreens.RecoveryCodeEntry]: RecoveryCodeEntryScreen,
    [AppScreens.RecoveryCodeSave]: RecoveryCodeSaveScreen,
    [AppScreens.TOTPEnrollmentQRCode]: TOTPEnrollQRScreen,
    [AppScreens.TOTPEnrollmentManual]: TOTPEnrollManualScreen,
    [AppScreens.TOTPEntry]: TOTPEntryScreen,
  };

  const ScreenComponent = defaultScreenComponents[currentScreen] ?? screenComponentMap[currentScreen];

  return (
    <MainContainer theme={theme} displayWatermark={displayWatermark}>
      <Logo appLogo={options.logo} orgLogo={orgLogo} />
      <ScreenComponent />
    </MainContainer>
  );
};

export default AppContainer;
